/* CIP AI Platform — embedded from cip-builder; Bedrock + CIX/RAER against configurable API origin. */
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Pako from "pako";
import { SYS } from "./cipAiPlatformSysPrompt";

export interface CipAiPlatformAppProps {
  /** CIP API origin (e.g. https://azdemo.api.cloudintelligence.industrycloud.pwc.com). Empty = same-origin / Vite proxy. */
  apiBaseUrl?: string;
  /** Pre-fill CIX Bearer token from shell login. */
  authToken?: string;
}

const BK = process.env.REACT_APP_BEDROCK_API_KEY || "";
const BM = process.env.REACT_APP_BEDROCK_MODEL_ID || "bedrock.anthropic.claude-sonnet-4-6";
const BEDROCK_MESSAGES_URL = process.env.REACT_APP_BEDROCK_MESSAGES_URL || "/v1/messages";

interface CipRequestOptions {
  method?: string;
  body?: unknown;
  cix?: boolean;
  headers?: Record<string, string>;
  /** Send JSON as gzip bytes (Content-Encoding: gzip) — CIP journey create/update. */
  gzipJson?: boolean;
}

type CipResponse = { ok: boolean; status: number; data: unknown };

function resolveApiPath(path: string, apiBaseUrl: string | undefined): string {
  if (typeof path === "string" && path.startsWith("http")) return path;
  const base = (apiBaseUrl || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

/** Resolved URL for logs when path is relative (same-origin / proxy). */
function displayUrlForLog(resolvedPath: string, apiBaseUrl: string | undefined): string {
  if (resolvedPath.startsWith("http")) return resolvedPath;
  if (typeof window !== "undefined" && resolvedPath.startsWith("/")) {
    return `${window.location.origin}${resolvedPath}`;
  }
  const base = (apiBaseUrl || "").replace(/\/$/, "");
  return base ? `${base}${resolvedPath}` : resolvedPath;
}

function cixHeadersForLog(h: Record<string, string>): Record<string, string> {
  const o = { ...h };
  if (o.Authorization) o.Authorization = "Bearer <redacted>";
  return o;
}

function mergeApprovePatchBody(b: unknown, status: "PENDING_APPROVAL" | "APPROVED"): Record<string, unknown> {
  if (typeof b === "object" && b !== null && !Array.isArray(b)) {
    return { ...(b as Record<string, unknown>), status };
  }
  return { status };
}

/**
 * Journey approve: CIX expects **`description` on the PENDING_APPROVAL PATCH**; the final **APPROVED** PATCH omits **`description`**.
 * If **`description`** is missing from the action body, **`comment`** is reused for the first PATCH only.
 */
function mergeApproveJourneyPatchBody(b: unknown, status: "PENDING_APPROVAL" | "APPROVED"): Record<string, unknown> {
  const m = mergeApprovePatchBody(b, status);
  if (status === "APPROVED") {
    const { description: _omit, ...rest } = m;
    return rest;
  }
  const desc = m.description;
  if (typeof desc === "string" && desc.trim() !== "") {
    return m;
  }
  const comment = m.comment;
  const fallback =
    typeof comment === "string" && comment.trim() !== "" ? comment.trim() : "Pending approval";
  return { ...m, description: fallback };
}

/** CIX: do not send `authDetails` when auth type is None / empty (matches Control Plane behavior). */
function omitAuthDetailsWhenAuthTypeNone(body: unknown): unknown {
  if (typeof body !== "object" || body === null || Array.isArray(body)) return body;
  const o = body as Record<string, unknown>;
  const ad = o.authDetails;
  if (!ad || typeof ad !== "object" || Array.isArray(ad)) return body;
  const authType = (ad as Record<string, unknown>).authType;
  const t = typeof authType === "string" ? authType.trim().toLowerCase() : "";
  const isNone =
    authType === undefined ||
    authType === null ||
    t === "" ||
    t === "none";
  if (!isNone) return body;
  const { authDetails: _omit, ...rest } = o;
  return rest;
}

/** Maps Control Plane service types to canonical CIX strings (ALL CAPS for REST, EXTERNAL, SOAP, DB2, MQ). */
function mapCanonicalServiceTypeString(t: string): string | null {
  const s = t.trim();
  if (/^rest$/i.test(s)) return "REST";
  if (/^external$/i.test(s)) return "EXTERNAL";
  if (/^soap$/i.test(s)) return "SOAP";
  if (/^db2$/i.test(s)) return "DB2";
  if (/^mq$/i.test(s)) return "MQ";
  return null;
}

/** CIX service request root `serviceType` — normalize known types to canonical ALL CAPS. */
function normalizeTopLevelServiceType(body: Record<string, unknown>): void {
  const st = body.serviceType;
  if (typeof st !== "string") return;
  const mapped = mapCanonicalServiceTypeString(st);
  if (mapped) body.serviceType = mapped;
}

const DEFAULT_INGRESS_CONFIG: Record<string, unknown> = {
  requestSpecJson: null,
  payloadToQueryPathSpecJson: null,
  payloadToRequestHeaderSpecJson: null,
  headerToRequestHeaderSpecJson: null,
  headerToRequestPayloadSpecJson: null,
  authSpecJson: null,
  mqRequestSpec: "",
  exceptionMappings: [],
};

/**
 * CIX expects every ingressConfig to include all keys; use null, "", or [] when unused.
 */
function normalizeIngressConfig(ic: unknown): Record<string, unknown> {
  const e = ic && typeof ic === "object" && !Array.isArray(ic) ? (ic as Record<string, unknown>) : {};
  const merged: Record<string, unknown> = { ...DEFAULT_INGRESS_CONFIG, ...e };
  merged.requestSpecJson = merged.requestSpecJson ?? null;
  merged.payloadToQueryPathSpecJson = merged.payloadToQueryPathSpecJson ?? null;
  merged.payloadToRequestHeaderSpecJson = merged.payloadToRequestHeaderSpecJson ?? null;
  merged.headerToRequestHeaderSpecJson = merged.headerToRequestHeaderSpecJson ?? null;
  merged.headerToRequestPayloadSpecJson = merged.headerToRequestPayloadSpecJson ?? null;
  merged.authSpecJson = merged.authSpecJson ?? null;
  merged.mqRequestSpec = merged.mqRequestSpec != null ? String(merged.mqRequestSpec) : "";
  merged.exceptionMappings = Array.isArray(merged.exceptionMappings) ? merged.exceptionMappings : [];
  return merged;
}

const DEFAULT_SERVICE_OPERATION_FIELDS: Record<string, unknown> = {
  requestAttributes: [],
  responseAttributes: [],
  security: "",
  queryPathParam: {},
  headerParam: {},
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** CIX expects these keys on each service operation even when empty. */
function normalizeServiceOperation(op: unknown): unknown {
  if (!isPlainObject(op)) return op;
  const merged: Record<string, unknown> = { ...DEFAULT_SERVICE_OPERATION_FIELDS, ...op };
  merged.requestAttributes = Array.isArray(merged.requestAttributes) ? merged.requestAttributes : [];
  merged.responseAttributes = Array.isArray(merged.responseAttributes) ? merged.responseAttributes : [];
  merged.security = merged.security != null ? String(merged.security) : "";
  merged.queryPathParam = isPlainObject(merged.queryPathParam) ? merged.queryPathParam : {};
  merged.headerParam = isPlainObject(merged.headerParam) ? merged.headerParam : {};
  return merged;
}

function normalizeServiceRequestBody(body: unknown): unknown {
  if (typeof body !== "object" || body === null || Array.isArray(body)) return body;
  const out = { ...(body as Record<string, unknown>) };
  normalizeTopLevelServiceType(out);
  const ops = out.serviceOperations;
  if (!Array.isArray(ops)) return out;
  out.serviceOperations = ops.map((op) => {
    const normalized = normalizeServiceOperation(op);
    if (!isPlainObject(normalized)) return normalized;
    const opObj = normalized;
    const ingressEvents = opObj.ingressEvents;
    if (!Array.isArray(ingressEvents)) return opObj;
    return {
      ...opObj,
      ingressEvents: ingressEvents.map((ev) => {
        if (typeof ev !== "object" || ev === null || Array.isArray(ev)) return ev;
        const evObj = ev as Record<string, unknown>;
        return {
          ...evObj,
          ingressConfig: normalizeIngressConfig(evObj.ingressConfig),
        };
      }),
    };
  });
  return out;
}

function genJourneyNodeId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `NID-${crypto.randomUUID().toUpperCase()}`;
  }
  return `NID-${`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`.toUpperCase()}`;
}

function normalizeJourneyServiceType(v: unknown): string {
  if (typeof v !== "string" || !v.trim()) return "REST";
  const t = v.trim();
  const mapped = mapCanonicalServiceTypeString(t);
  if (mapped) return mapped;
  return t;
}

function isJourneyServiceRow(o: unknown): o is Record<string, unknown> {
  return isPlainObject(o) && typeof o.eventName === "string" && typeof o.serviceName === "string";
}

function getJourneyFirstStepKey(sd: Record<string, unknown>): string {
  if ("step" in sd) return "step";
  if ("step-1" in sd) return "step-1";
  const keys = Object.keys(sd);
  return keys.length > 0 ? keys[0] : "step";
}

/** Prefer `firstStepName`, else first `journeyJsonDetails.stepNodes[0].stepName`. */
function getCanonicalStepNameFromBody(b: Record<string, unknown>): string | null {
  const fs = typeof b.firstStepName === "string" && b.firstStepName.trim() ? b.firstStepName.trim() : null;
  if (fs) return fs;
  const jjd = b.journeyJsonDetails;
  if (isPlainObject(jjd) && Array.isArray(jjd.stepNodes) && jjd.stepNodes.length > 0) {
    const sn0 = jjd.stepNodes[0];
    if (isPlainObject(sn0) && typeof sn0.stepName === "string" && sn0.stepName.trim()) {
      return sn0.stepName.trim();
    }
  }
  return null;
}

/**
 * When the user chose a step name (e.g. `step1`), ensure `stepDetails[thatName]` holds the map — migrate a single misplaced key.
 */
function reconcileStepDetailsCanonicalKey(
  sd: Record<string, unknown> | undefined,
  canonical: string | null,
): Record<string, unknown> {
  if (!canonical || !isPlainObject(sd)) return isPlainObject(sd) ? sd : {};
  if (isPlainObject(sd[canonical])) return sd;
  const keys = Object.keys(sd).filter((k) => isPlainObject(sd[k]));
  if (keys.length === 1 && keys[0] !== canonical) {
    const only = sd[keys[0]] as Record<string, unknown>;
    const next = { ...sd };
    delete next[keys[0]];
    next[canonical] = only;
    return next;
  }
  return sd;
}

/** Linear chain: node₁ → node₂ → … using ingress/egress; single node → []. Egress items: `nodeId` + `external` only (no `mep`). */
function buildSequentialEventMapEdges(nodes: Record<string, unknown>[]): Record<string, unknown>[] {
  if (nodes.length < 2) return [];
  const edges: Record<string, unknown>[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    const from = nodes[i]?.nodeId;
    const to = nodes[i + 1]?.nodeId;
    if (typeof from !== "string" || typeof to !== "string") continue;
    edges.push({
      ingress: [{ nodeId: from }],
      egress: [{ nodeId: to, external: false }],
    });
  }
  return edges;
}

function journeyServiceRowToNode(row: unknown, index: number): Record<string, unknown> | null {
  if (!isJourneyServiceRow(row)) return null;
  const r = row;
  return {
    nodeId: genJourneyNodeId(),
    eventName: r.eventName,
    status: typeof r.status === "string" && r.status ? r.status : "not started",
    serviceName: r.serviceName,
    serviceOperationName:
      typeof r.serviceOperationName === "string" && r.serviceOperationName ? r.serviceOperationName : "so",
    serviceDescription: typeof r.serviceDescription === "string" ? r.serviceDescription : "",
    serviceType: normalizeJourneyServiceType(r.serviceType),
    xCoordinate: typeof r.xCoordinate === "number" ? r.xCoordinate : 250 + (index % 4) * 200,
    yCoordinate: typeof r.yCoordinate === "number" ? r.yCoordinate : 40 + Math.floor(index / 4) * 90,
    isCompensateAction: typeof r.isCompensateAction === "boolean" ? r.isCompensateAction : false,
    serviceVersion: typeof r.serviceVersion === "number" ? r.serviceVersion : 1,
    eventVersion: typeof r.eventVersion === "number" ? r.eventVersion : 1,
  };
}

function finalizeEventMapNode(n: unknown, index: number): Record<string, unknown> {
  if (!isPlainObject(n)) return {};
  const o = { ...n };
  if (typeof o.nodeId !== "string" || !o.nodeId) o.nodeId = genJourneyNodeId();
  if (typeof o.status !== "string" || !o.status) o.status = "not started";
  if (typeof o.serviceDescription !== "string") o.serviceDescription = "";
  o.serviceType = normalizeJourneyServiceType(o.serviceType);
  if (typeof o.xCoordinate !== "number") o.xCoordinate = 250 + (index % 4) * 200;
  if (typeof o.yCoordinate !== "number") o.yCoordinate = 40 + Math.floor(index / 4) * 90;
  if (typeof o.isCompensateAction !== "boolean") o.isCompensateAction = false;
  if (typeof o.serviceVersion !== "number") o.serviceVersion = 1;
  if (typeof o.eventVersion !== "number") o.eventVersion = 1;
  if (typeof o.serviceOperationName !== "string" || !o.serviceOperationName) o.serviceOperationName = "so";
  return o;
}

function deriveCountsFromEventMapNodes(nodes: Record<string, unknown>[]): {
  eventsCount: number;
  servicesCount: number;
  cemCount: number;
} {
  const ev = new Set<string>();
  const sv = new Set<string>();
  let cem = 0;
  for (const n of nodes) {
    if (typeof n.eventName === "string" && n.eventName) ev.add(n.eventName);
    if (typeof n.serviceName === "string" && n.serviceName) sv.add(n.serviceName);
    if (n.isCompensateAction === true) cem += 1;
  }
  const n = nodes.length;
  return {
    eventsCount: ev.size > 0 ? ev.size : n,
    servicesCount: sv.size > 0 ? sv.size : n,
    cemCount: cem,
  };
}

/**
 * - Merges `journeyServices` / qualifying `services` into **`stepDetails[firstStepName]`** (e.g. `step1`); same name is used in **`journeyJsonDetails.stepNodes[0].stepName`**.
 * - **`eventMapNodes[].nodeId`**: unique `NID-{UUID}`. One service → **`eventMapEdges`**: `[]`. Two+ services: by default, sequential **linear 1:1** **`eventMapEdges`** (no **`mep`**). If **`eventMapEdges`** is **already** a **non-empty** **array** (user/model DAG, **conditionMap**, **convergence** join with **`ingressOperation`**, **`payloadTransformations`**, optional **`mep`**, etc.), that array is **preserved** as-is. **`startNode`** is removed — not sent to CIX.
 * - Fills `journeyJsonDetails` when `stepNodes` is empty; **`stepNodeId`** on the canvas step is a new `NID-{UUID}`.
 * - **`eventsCount`**, **`servicesCount`**, **`cemCount`** at root (optional): override counts; otherwise derived from nodes.
 * - Strips helper-only keys before send.
 * - **`stepAction`** on the first `stepNodes` entry: `"create"` | `"update"`.
 */
function normalizeJourneyRequestBody(body: unknown, journeyIntent: "create" | "update"): unknown {
  if (!isPlainObject(body)) return body;
  const b = { ...body };
  const stepAction = journeyIntent === "create" ? "create" : "update";
  const canonical = getCanonicalStepNameFromBody(b);

  let injectedRows: unknown[] | null = null;
  let stripKey: "journeyServices" | "services" | null = null;
  if (Array.isArray(b.journeyServices) && b.journeyServices.length > 0) {
    injectedRows = b.journeyServices;
    stripKey = "journeyServices";
  } else if (
    Array.isArray(b.services) &&
    b.services.length > 0 &&
    (b.services as unknown[]).every(isJourneyServiceRow)
  ) {
    injectedRows = b.services;
    stripKey = "services";
  }

  if (injectedRows) {
    const stepDetailsPrev = isPlainObject(b.stepDetails) ? { ...b.stepDetails } : {};
    const stepKey =
      canonical ??
      (Object.keys(stepDetailsPrev).length > 0 ? getJourneyFirstStepKey(stepDetailsPrev) : "step");
    const nodes = injectedRows
      .map((row, i) => journeyServiceRowToNode(row, i))
      .filter((x): x is Record<string, unknown> => x !== null);
    if (nodes.length > 0) {
      const prev = isPlainObject(stepDetailsPrev[stepKey]) ? (stepDetailsPrev[stepKey] as Record<string, unknown>) : {};
      stepDetailsPrev[stepKey] = {
        ...prev,
        eventMapMaxExecutionTimeInSec:
          prev.eventMapMaxExecutionTimeInSec === undefined ? null : prev.eventMapMaxExecutionTimeInSec,
        eventMapNodes: nodes,
        eventMapEdges: [],
      };
      b.stepDetails = stepDetailsPrev;
    }
    if (stripKey) delete b[stripKey];
  }

  if (isPlainObject(b.stepDetails)) {
    b.stepDetails = reconcileStepDetailsCanonicalKey(b.stepDetails as Record<string, unknown>, canonical);
  }

  if (isPlainObject(b.stepDetails)) {
    const sd = { ...b.stepDetails } as Record<string, unknown>;
    for (const key of Object.keys(sd)) {
      const step = sd[key];
      if (!isPlainObject(step)) continue;
      const nodes = step.eventMapNodes;
      if (Array.isArray(nodes)) {
        const finalized = nodes.map((node, i) => finalizeEventMapNode(node, i));
        step.eventMapNodes = finalized;
        const preEdges = step.eventMapEdges;
        if (Array.isArray(preEdges) && preEdges.length > 0) {
          step.eventMapEdges = preEdges;
        } else {
          step.eventMapEdges = buildSequentialEventMapEdges(finalized as Record<string, unknown>[]);
        }
      } else {
        step.eventMapEdges = [];
      }
      if (step.eventMapMaxExecutionTimeInSec === undefined) step.eventMapMaxExecutionTimeInSec = null;
      delete step.startNode;
      sd[key] = step;
    }
    b.stepDetails = sd;
  }

  const firstStepDescription =
    typeof b.firstStepDescription === "string" ? b.firstStepDescription : "";

  const sd = b.stepDetails;
  if (isPlainObject(sd)) {
    const stepKey = canonical ?? getJourneyFirstStepKey(sd);
    const step = sd[stepKey];
    if (isPlainObject(step)) {
      const eventMapNodes = step.eventMapNodes;
      if (Array.isArray(eventMapNodes) && eventMapNodes.length > 0) {
        const nodes = eventMapNodes as Record<string, unknown>[];
        const derived = deriveCountsFromEventMapNodes(nodes);
        const eventsCount =
          typeof b.eventsCount === "number" && !Number.isNaN(b.eventsCount) ? b.eventsCount : derived.eventsCount;
        const servicesCount =
          typeof b.servicesCount === "number" && !Number.isNaN(b.servicesCount)
            ? b.servicesCount
            : derived.servicesCount;
        const cemCount =
          typeof b.cemCount === "number" && !Number.isNaN(b.cemCount) ? b.cemCount : derived.cemCount;

        const jjd = isPlainObject(b.journeyJsonDetails) ? { ...b.journeyJsonDetails } : {};
        const existing = Array.isArray(jjd.stepNodes) ? jjd.stepNodes : [];
        const displayStepName = stepKey;

        const firstExisting = existing[0] && isPlainObject(existing[0]) ? (existing[0] as Record<string, unknown>) : null;
        const stepNodePayload: Record<string, unknown> = {
          stepNodeId:
            typeof firstExisting?.stepNodeId === "string" && firstExisting.stepNodeId
              ? firstExisting.stepNodeId
              : genJourneyNodeId(),
          stepName: displayStepName,
          stepDescription: firstStepDescription,
          stepAction,
          eventsCount,
          servicesCount,
          cemCount,
          xCoordinate: 50,
          yCoordinate: 40,
        };

        if (existing.length === 0) {
          b.journeyJsonDetails = {
            ...jjd,
            stepNodes: [stepNodePayload],
            stepEdges: Array.isArray(jjd.stepEdges) ? jjd.stepEdges : [],
          };
        } else {
          const mergedFirst = { ...firstExisting, ...stepNodePayload };
          b.journeyJsonDetails = {
            ...jjd,
            stepNodes: [mergedFirst, ...existing.slice(1)],
            stepEdges: Array.isArray(jjd.stepEdges) ? jjd.stepEdges : [],
          };
        }
      }
    }
  }

  delete b.firstStepName;
  delete b.firstStepDescription;
  delete b.eventsCount;
  delete b.servicesCount;
  delete b.cemCount;

  return b;
}

function createCipClient(apiBaseUrl: string, getCixToken: () => string) {
  const RK = process.env.REACT_APP_RAER_API_KEY || "";
  const toQuery = (qp?: Record<string, string | number | boolean | undefined | null>) => {
    const params = new URLSearchParams();
    Object.entries(qp || {}).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      params.set(k, String(v));
    });
    const q = params.toString();
    return q ? `?${q}` : "";
  };
  async function cip(path: string, opts: CipRequestOptions = {}): Promise<CipResponse> {
    const { method = "GET", body, cix = false, headers: x = {}, gzipJson = false } = opts;
    const auth: Record<string, string> = cix
      ? { Authorization: `Bearer ${getCixToken()}` }
      : { "X-Api-Key": RK };
    const headersMerged: Record<string, string> = {
      "Content-Type": "application/json",
      ...auth,
      ...x,
    };
    if (gzipJson && body !== undefined && body !== null) {
      headersMerged["Content-Encoding"] = "gzip";
    }
    const init: RequestInit = { method, headers: headersMerged };
    if (body !== undefined && body !== null) {
      init.body = gzipJson ? Pako.gzip(JSON.stringify(body)) : JSON.stringify(body);
    }
    const resolved = resolveApiPath(path, apiBaseUrl);
    if (cix) {
      const url = displayUrlForLog(resolved, apiBaseUrl);
      const logBody =
        gzipJson && body !== undefined && body !== null
          ? { _encoding: "gzip", json: body }
          : body ?? null;
       
      console.info("[CIX request]", { method, url, headers: cixHeadersForLog(headersMerged), body: logBody });
    }
    const r = await fetch(resolved, init);
    const t = await r.text();
    let d;
    try {
      d = JSON.parse(t);
    } catch {
      d = { raw: t };
    }
    return { ok: r.ok, status: r.status, data: d };
  }
  const RAER = (p: unknown, h: Record<string, string>) =>
    cip("/cip/api/v2/service-handler-raer/invoke", { method: "POST", body: p, headers: h });
  const RAER_A = (p: unknown, h: Record<string, string>) =>
    cip("/cip/api/v2/service-handler-raer/invoke/async", { method: "POST", body: p, headers: h });
  const CX = {
    createEvent: (b: unknown) => cip("/cip/api/v2/cix/events", { method: "POST", body: b, cix: true }),
    getEvent: (n: string, v: string | number) =>
      cip(`/cip/api/v2/cix/events/${encodeURIComponent(n)}/version/${v}`, { cix: true }),
    updateEvent: (n: string, v: string | number, b: unknown) =>
      cip(`/cip/api/v2/cix/events/${encodeURIComponent(n)}/version/${v}`, { method: "PUT", body: b, cix: true }),
    approveEvent: async (n: string, v: string | number, b: unknown) => {
      const path = `/cip/api/v2/cix/events/${encodeURIComponent(n)}/version/${v}`;
      const r1 = await cip(path, {
        method: "PATCH",
        body: mergeApprovePatchBody(b, "PENDING_APPROVAL"),
        cix: true,
      });
      if (!r1.ok) return r1;
      return cip(path, { method: "PATCH", body: mergeApprovePatchBody(b, "APPROVED"), cix: true });
    },
    searchEvents: (b: unknown) =>
      cip("/cip/api/v2/cix/events/search?page=0&size=100", { method: "POST", body: b, cix: true }),
    createService: (b: unknown) =>
      cip("/cip/api/v2/cix/services", {
        method: "POST",
        body: normalizeServiceRequestBody(omitAuthDetailsWhenAuthTypeNone(b)),
        cix: true,
      }),
    getService: (n: string, v: string | number) =>
      cip(`/cip/api/v2/cix/services/${encodeURIComponent(n)}/version/${v}`, { cix: true }),
    updateService: (n: string, v: string | number, b: unknown) =>
      cip(`/cip/api/v2/cix/services/${encodeURIComponent(n)}/version/${v}`, {
        method: "PUT",
        body: normalizeServiceRequestBody(omitAuthDetailsWhenAuthTypeNone(b)),
        cix: true,
      }),
    approveService: async (n: string, v: string | number, b: unknown) => {
      const path = `/cip/api/v2/cix/services/${encodeURIComponent(n)}/version/${v}`;
      const r1 = await cip(path, {
        method: "PATCH",
        body: mergeApprovePatchBody(b, "PENDING_APPROVAL"),
        cix: true,
      });
      if (!r1.ok) return r1;
      return cip(path, { method: "PATCH", body: mergeApprovePatchBody(b, "APPROVED"), cix: true });
    },
    searchServices: (b: unknown) =>
      cip("/cip/api/v2/cix/services/search?page=0&size=100", { method: "POST", body: b, cix: true }),
    createJourney: (b: unknown) =>
      cip("/cip/api/v2/cix/journeys", {
        method: "POST",
        body: normalizeJourneyRequestBody(b, "create"),
        cix: true,
        gzipJson: true,
      }),
    getJourney: (n: string, v: string | number) =>
      cip(`/cip/api/v2/cix/journeys/${encodeURIComponent(n)}/version/${v}`, { cix: true }),
    updateJourney: (n: string, v: string | number, b: unknown) =>
      cip(`/cip/api/v2/cix/journeys/${encodeURIComponent(n)}/version/${v}`, {
        method: "PUT",
        body: normalizeJourneyRequestBody(b, "update"),
        cix: true,
        gzipJson: true,
      }),
    approveJourney: async (n: string, v: string | number, b: unknown) => {
      const path = `/cip/api/v2/cix/journeys/${encodeURIComponent(n)}/version/${v}`;
      const r1 = await cip(path, {
        method: "PATCH",
        body: mergeApproveJourneyPatchBody(b, "PENDING_APPROVAL"),
        cix: true,
      });
      if (!r1.ok) return r1;
      return cip(path, { method: "PATCH", body: mergeApproveJourneyPatchBody(b, "APPROVED"), cix: true });
    },
    searchJourneys: (b: unknown) =>
      cip("/cip/api/v2/cix/journeys/search?page=0&size=100", { method: "POST", body: b, cix: true }),
    createSource: (b: unknown) =>
      cip("/cip/api/v2/cix/sources", { method: "POST", body: Array.isArray(b) ? b : [b], cix: true }),
    createConnector: (b: unknown) => cip("/cip/api/v2/cix/connectors", { method: "POST", body: b, cix: true }),
    createException: (b: unknown) =>
      cip("/cip/api/v2/cix/exceptions", { method: "POST", body: Array.isArray(b) ? b : [b], cix: true }),
    searchExceptions: (b: unknown) =>
      cip("/cip/api/v2/cix/exceptions/search?page=0&size=100", { method: "POST", body: b, cix: true }),
    searchDlq: (b: unknown) =>
      cip("/cip/api/v2/cix/dlq/search?page=0&size=100", { method: "POST", body: b, cix: true }),
    searchDlqJourneys: (b: unknown, qp?: Record<string, string | number>) => {
      const qs = new URLSearchParams({
        page: "0",
        size: "100",
        ...Object.fromEntries(Object.entries(qp || {}).map(([k, v]) => [k, String(v)])),
      });
      return cip(`/cip/api/v2/cix/dlq/search/journey?${qs}`, { method: "POST", body: b, cix: true });
    },
    reprocessDlq: (b: unknown) =>
      cip("/cip/api/v2/cix/dlq/reprocess", {
        method: "POST",
        body: { reprocessMessages: Array.isArray(b) ? b : [b] },
        cix: true,
      }),
    getScheduleMaintenance: (p?: Record<string, string>) => {
      const qs = p ? `?${new URLSearchParams(p)}` : "";
      return cip(`/cip/api/v2/cix/schedule-maintenance${qs}`, { cix: true });
    },
    getMaintenanceAlerts: (o: string | undefined, s: string | undefined) =>
      cip(`/cip/api/v2/cix/schedule-maintenance/maintenance-alert?offset=${o || ""}&maintenanceStatus=${s || ""}`, {
        cix: true,
      }),
    getAllJobs: (b: unknown) =>
      cip("/cip/api/v2/cix/scheduler/schedule-journey/fetchAllJobs?page=0&size=100", { method: "POST", body: b, cix: true }),
    getAllStatusCounts: () => cip("/cip/api/v2/cix/status-count", { cix: true }),
    statusFilter: (qp?: Record<string, string | number | boolean | undefined | null>) =>
      cip(`/cip/api/v2/fsm-service/status-filter${toQuery(qp)}`, { cix: true }),
    statusFilterByJourneyInstances: async (b: unknown) => {
      if (typeof b !== "object" || b === null || Array.isArray(b)) {
        return { ok: false, status: 400, data: { error: "body must be an object with journeyInstanceIds[]" } };
      }
      const body = b as Record<string, unknown>;
      const ids = Array.isArray(body.journeyInstanceIds)
        ? body.journeyInstanceIds.map((x) => String(x).trim()).filter(Boolean)
        : [];
      if (!ids.length) {
        return { ok: false, status: 400, data: { error: "journeyInstanceIds[] is required" } };
      }
      const baseQp = {
        status: typeof body.status === "string" ? body.status : "in_progress,failed,completed",
        page: typeof body.page === "number" ? body.page : 0,
        size: typeof body.size === "number" ? body.size : 50,
        sort:
          typeof body.sort === "string" && body.sort.trim()
            ? body.sort
            : "minStateTransitionTimestamp,DESC",
      };
      const results = await Promise.all(
        ids.map(async (journeyInstanceId) => {
          const r = await cip(
            `/cip/api/v2/fsm-service/status-filter${toQuery({ ...baseQp, "journey-instance-id": journeyInstanceId })}`,
            { cix: true },
          );
          return { journeyInstanceId, ok: r.ok, status: r.status, data: r.data };
        }),
      );
      const ok = results.every((x) => x.ok);
      return {
        ok,
        status: ok ? 200 : 207,
        data: {
          total: results.length,
          successCount: results.filter((x) => x.ok).length,
          errorCount: results.filter((x) => !x.ok).length,
          results,
        },
      };
    },
    statusFilterByEventMapInstances: async (b: unknown) => {
      if (typeof b !== "object" || b === null || Array.isArray(b)) {
        return { ok: false, status: 400, data: { error: "body must be an object with eventMapInstanceIds[]" } };
      }
      const body = b as Record<string, unknown>;
      const ids = Array.isArray(body.eventMapInstanceIds)
        ? body.eventMapInstanceIds.map((x) => String(x).trim()).filter(Boolean)
        : [];
      if (!ids.length) {
        return { ok: false, status: 400, data: { error: "eventMapInstanceIds[] is required" } };
      }
      const baseQp = {
        status: typeof body.status === "string" ? body.status : "not_started,in_progress,failed,completed",
        page: typeof body.page === "number" ? body.page : 0,
        size: typeof body.size === "number" ? body.size : 50,
        sort:
          typeof body.sort === "string" && body.sort.trim()
            ? body.sort
            : "minStateTransitionTimestamp,DESC",
      };
      const results = await Promise.all(
        ids.map(async (eventMapInstanceId) => {
          const r = await cip(
            `/cip/api/v2/fsm-service/status-filter${toQuery({ ...baseQp, "event-map-instance-id": eventMapInstanceId })}`,
            { cix: true },
          );
          return { eventMapInstanceId, ok: r.ok, status: r.status, data: r.data };
        }),
      );
      const ok = results.every((x) => x.ok);
      return {
        ok,
        status: ok ? 200 : 207,
        data: {
          total: results.length,
          successCount: results.filter((x) => x.ok).length,
          errorCount: results.filter((x) => !x.ok).length,
          results,
        },
      };
    },
  };
  return { RAER, RAER_A, CX };
}

// ── Helpers ─────────────────────────────────────────────────
const gid = (p: string) =>
  `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`.toUpperCase();

function parseAction(t: string): { text: string; action: unknown } {
  const m = t.match(/```action\s*\n?([\s\S]*?)\n?```/);
  if (!m) return { text: t, action: null };
  try {
    return { text: t.replace(/```action[\s\S]*?```/, "").trim(), action: JSON.parse(m[1]) };
  } catch {
    return { text: t, action: null };
  }
}

// ── Capability definitions for UI ───────────────────────────
const CAPS = [
  { id: "build", icon: "⚡", label: "Build Journey", desc: "Create events, services & journeys step by step", color: "#6366f1" },
  { id: "schema", icon: "📐", label: "Schema Generator", desc: "Avro schemas from natural language or JSON", color: "#8b5cf6" },
  { id: "connector", icon: "🔌", label: "Connector", desc: "Configure external system integrations", color: "#a855f7" },
  { id: "trigger", icon: "🚀", label: "Trigger", desc: "Execute journeys via RAER", color: "#0ea5e9" },
  { id: "fraud", icon: "🛡️", label: "Fraud Detection", desc: "Risk scoring for transactions", color: "#f43f5e" },
  { id: "observe", icon: "📊", label: "Observability", desc: "Health checks, DLQ, maintenance", color: "#10b981" },
  { id: "rca", icon: "🔍", label: "Root Cause", desc: "Investigate why failures happened", color: "#f59e0b" },
  { id: "predict", icon: "🔮", label: "Predict", desc: "Forecast failures & capacity", color: "#ec4899" },
  { id: "docs", icon: "📝", label: "Documentation", desc: "Generate journey & service docs", color: "#06b6d4" },
  { id: "optimize", icon: "⚙️", label: "Optimize", desc: "Journey complexity & performance", color: "#84cc16" },
  { id: "decide", icon: "🧠", label: "Smart Routing", desc: "AI-powered flow decisions", color: "#d946ef" },
];

const CAP_PROMPTS: Record<(typeof CAPS)[number]["id"], string> = {
  build: "I want to build a new journey from scratch. Guide me through it step by step.",
  schema: "I need to create an Avro schema for a new CIP event. Help me generate it.",
  connector: "I want to configure a connector for an external system. Walk me through it.",
  trigger: "I want to trigger an existing CIP journey via RAER.",
  fraud: "I want to add fraud detection to a payment journey. Help me set it up.",
  observe: "Give me a health check of the CIP platform — DLQ, maintenance, job status.",
  rca: "I have journey failures. Help me investigate the root cause.",
  predict: "Predict what's likely to fail next. Run a full risk assessment.",
  docs: "I need documentation generated for a CIP journey or service.",
  optimize: "Analyze my journeys for optimization opportunities.",
  decide: "I need intelligent routing — help me decide which journey to trigger based on data.",
};

/** `true` = hide header RAER/CIX/key/Chat|Log and the API log column. State, `addL`, and actions are unchanged. Set `false` to show again. */
const HIDE_CIP_PLATFORM_CHROME = true;

type SysEvt = "ok" | "err" | "run";

interface ChatMsg {
  role: "user" | "ai" | "sys";
  content: string;
  ts?: Date;
  evt?: SysEvt;
}

interface CixActionPayload {
  type: "CIX";
  api: string;
  args?: unknown[];
  body?: unknown;
  queryParams?: Record<string, string | number>;
}

interface LogEntry {
  type: string;
  ts: string;
  api?: string;
  mode?: string;
  status?: number;
  body?: unknown;
  args?: unknown[];
  queryParams?: Record<string, string | number>;
  payload?: unknown;
  headers?: Record<string, string>;
  data?: unknown;
}

// ── App ─────────────────────────────────────────────────────
const CipAiPlatformApp = ({ apiBaseUrl = "", authToken = "" }: CipAiPlatformAppProps) => {
  const [cixToken, setCixToken] = useState("");
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCaps, setShowCaps] = useState(true);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [view, setView] = useState<"chat" | "log">("chat");
  const [tkInput, setTkInput] = useState("");
  const [showTk, setShowTk] = useState(false);
  const end = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (authToken) setCixToken(authToken);
  }, [authToken]);

  const { RAER, RAER_A, CX } = useMemo(
    () => createCipClient(apiBaseUrl, () => cixToken),
    [apiBaseUrl, cixToken],
  );

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const add = useCallback((m: Omit<ChatMsg, "ts">) => {
    setMsgs((p) => [...p, { ...m, ts: new Date() }]);
  }, []);

  const addL = useCallback((e: Omit<LogEntry, "ts">) => {
    setLog((p) => [...p, { ...e, ts: new Date().toISOString() }]);
  }, []);

  const saveTk = () => {
    setCixToken(tkInput.trim());
    setTkInput("");
    setShowTk(false);
    add({ role: "sys", content: "CIX token saved — you can now create and query CIP entities.", evt: "ok" });
  };

  // ── Execute RAER ──
  const doRAER = useCallback(
    async (a: { mode: string; payload: unknown; headers: Record<string, string> }) => {
      const h = { ...a.headers };
      if (h.correlationId === "auto") h.correlationId = gid("COR");
      addL({ type: "RAER_REQ", mode: a.mode, headers: h, payload: a.payload });
      add({ role: "sys", content: `RAER ${a.mode} → ${h.eventName} on ${h.journeyName}`, evt: "run" });
      const r = await (a.mode === "async" ? RAER_A : RAER)(a.payload, h);
      addL({ type: r.ok ? "RAER_OK" : "RAER_ERR", status: r.status, data: r.data });
      add({ role: "sys", content: `RAER → HTTP ${r.status}`, evt: r.ok ? "ok" : "err" });
      return r;
    },
    [add, addL, RAER, RAER_A],
  );

  // ── Execute CIX ──
  const doCIX = useCallback(
    async (a: CixActionPayload) => {
      if (!cixToken) {
        add({ role: "sys", content: "CIX token required — paste your Bearer token in the field below", evt: "err" });
        setShowTk(true);
        return null;
      }
      const fn = CX[a.api as keyof typeof CX];
      if (!fn) {
        add({ role: "sys", content: `Unknown API: ${a.api}`, evt: "err" });
        return null;
      }
      addL({ type: "CIX_REQ", api: a.api, body: a.body, args: a.args, queryParams: a.queryParams });
      add({ role: "sys", content: `CIX: ${a.api}...`, evt: "run" });
      const callFn = fn as (...args: unknown[]) => Promise<CipResponse>;
      let r: CipResponse;
      if (a.api === "searchDlqJourneys") {
        r = await CX.searchDlqJourneys(a.body, a.queryParams);
      } else if (a.api === "statusFilter") {
        r = await CX.statusFilter(a.queryParams);
      } else if (a.api === "getScheduleMaintenance") {
        r = await CX.getScheduleMaintenance(a.body as Record<string, string> | undefined);
      } else if (a.args && a.args.length > 0) {
        r =
          a.body !== undefined
            ? await callFn(...a.args, a.body)
            : await (callFn as (...x: unknown[]) => Promise<CipResponse>)(...a.args);
      } else if (a.body !== undefined) {
        r = await callFn(a.body);
      } else {
        r = await (callFn as () => Promise<CipResponse>)();
      }
      addL({ type: r.ok ? "CIX_OK" : "CIX_ERR", api: a.api, status: r.status, data: r.data });
      add({ role: "sys", content: `${a.api} → HTTP ${r.status}`, evt: r.ok ? "ok" : "err" });
      if (r.status === 401) {
        add({ role: "sys", content: "Token expired — click 🔑 to refresh", evt: "err" });
        setShowTk(true);
      }
      return r;
    },
    [add, addL, CX, cixToken],
  );

  // ── Call Claude ──
  const ask = useCallback(
    async (msg: string, sys = false) => {
      setBusy(true);
      setShowCaps(false);
      // Full session history (no trim) so long journey builds keep service chain, registry, and CIP results. Model/API context limits may still apply upstream.
      const hist = msgs
        .filter((m) => m.role === "user" || m.role === "ai")
        .map((m) => ({ role: m.role === "ai" ? "assistant" : "user", content: m.content }));
      try {
        const res = await fetch(BEDROCK_MESSAGES_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": BK, "anthropic-version": "2023-06-01" },
          body: JSON.stringify({
            model: BM,
            max_tokens: 4096,
            system: SYS,
            messages: [...hist, { role: "user", content: sys ? `[SYSTEM] ${msg}` : msg }],
          }),
        });
        if (!res.ok) throw new Error(`Bedrock ${res.status}`);
        const d = (await res.json()) as {
          content?: Array<{ text?: string }>;
          completion?: string;
        };
        const raw =
          (d.content || []).map((b: { text?: string }) => b.text || "").join("") || d.completion || "";
        const { text, action } = parseAction(raw);
        if (text) add({ role: "ai", content: text });
        if (action && typeof action === "object" && action !== null && "type" in action) {
          const act = action as { type?: string; api?: string; mode?: string };
          let r: CipResponse | null = null;
          if (act.type === "RAER") {
            r = await doRAER(action as unknown as { mode: string; payload: unknown; headers: Record<string, string> });
          } else if (act.type === "CIX") {
            r = await doCIX(action as CixActionPayload);
          }
          if (r) {
            const ctx = `${act.type} ${act.api || act.mode} completed. HTTP ${r.status}. Response: ${JSON.stringify(r.data).substring(0, 3000)}. ${r.ok ? "Summarize what happened and ask what to do next." : "Explain the error clearly."}`;
            await ask(ctx, true);
            return;
          }
        }
      } catch (e: unknown) {
        add({ role: "ai", content: `Error: ${e instanceof Error ? e.message : String(e)}` });
      }
      setBusy(false);
    },
    [msgs, add, doRAER, doCIX],
  );

  const send = (t?: string) => {
    const m = (t ?? input).trim();
    if (!m || busy) return;
    setInput("");
    add({ role: "user", content: m });
    void ask(m);
  };

  // ── Setup Screen ──
  if (!BK || BK === "your-bedrock-key") return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", maxWidth: 520, margin: "10vh auto", padding: "0 24px" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ display: "inline-flex", width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg,#6366f1,#a855f7,#ec4899)", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: "0 8px 32px rgba(99,102,241,.3)" }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-.02em" }}>CIP AI Platform</h1>
        <p style={{ fontSize: 14, color: "#71717a", marginTop: 4 }}>11 AI capabilities • 1 codebase</p>
      </div>
      <div style={{ padding: 24, borderRadius: 14, border: "1px solid #e4e4e7", background: "#fff" }}>
        <p style={{ fontSize: 13, color: "#71717a", margin: "0 0 14px" }}>Create <code style={{ background: "#f4f4f5", padding: "2px 6px", borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }}>.env</code> and add:</p>
        <div style={{ background: "#18181b", borderRadius: 10, padding: "16px 20px", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#d4d4d8", lineHeight: 2.2 }}>
          <div><span style={{ color: "#71717a" }}>REACT_APP_BEDROCK_API_KEY=</span><span style={{ color: "#facc15" }}>your-key</span></div>
          <div><span style={{ color: "#71717a" }}>REACT_APP_RAER_API_KEY=</span><span style={{ color: "#4ade80" }}></span></div>
        </div>
        <p style={{ fontSize: 12, color: "#a1a1aa", margin: "14px 0 0" }}>Then: <code style={{ fontFamily: "'JetBrains Mono',monospace" }}>npm install && npm start</code></p>
      </div>
    </div>
  );

  // ── Colors / Helpers ──
  const evtStyle: Record<SysEvt, { bg: string; fg: string; icon: string }> = {
    ok: { bg: "#ecfdf5", fg: "#065f46", icon: "✓" },
    err: { bg: "#fef2f2", fg: "#991b1b", icon: "✗" },
    run: { bg: "#eef2ff", fg: "#3730a3", icon: "⟳" },
  };

  return (
    <div
      style={{
        fontFamily: "'DM Sans',sans-serif",
        height: "100%",
        minHeight: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        background: "#fafafa",
      }}
    >
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
        @keyframes shimmer{0%{background-position:200%}100%{background-position:-200%}}
        *{box-sizing:border-box}
        .cap-btn{transition:all .15s ease;cursor:pointer;border:1.5px solid #e4e4e7;background:#fff}
        .cap-btn:hover{border-color:#a1a1aa;transform:translateY(-2px);box-shadow:0 6px 20px rgba(0,0,0,.06)}
        .msg-user{background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;border-radius:18px 18px 4px 18px}
        .msg-ai{background:#fff;border:1px solid #e4e4e7;border-radius:18px 18px 18px 4px;color:#18181b}
        .log-entry{transition:all .15s ease}
        .log-entry:hover{border-color:#a1a1aa}
        input:focus{border-color:#6366f1!important;box-shadow:0 0 0 3px rgba(99,102,241,.1)!important}
      `}</style>

      {/* ── Header ── */}
      <div style={{ padding: "12px 24px", borderBottom: "1px solid #e4e4e7", display: "flex", alignItems: "center", gap: 12, background: "#fff", flexShrink: 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#6366f1,#a855f7,#ec4899)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(99,102,241,.25)" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.01em" }}>CIP AI Platform</div>
          <div style={{ fontSize: 11, color: "#a1a1aa" }}>Build • Trigger • Monitor • Analyze</div>
        </div>
        <div
          style={{
            display: HIDE_CIP_PLATFORM_CHROME ? "none" : "flex",
            alignItems: "center",
            gap: 8,
            marginLeft: "auto",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 6, background: "#ecfdf5", color: "#065f46" }}>RAER ✓</span>
          <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 6, background: cixToken ? "#ecfdf5" : "#f4f4f5", color: cixToken ? "#065f46" : "#a1a1aa" }}>CIX {cixToken ? "✓" : "—"}</span>
          <button type="button" onClick={() => setShowTk((s) => !s)} style={{ all: "unset", cursor: "pointer", width: 32, height: 32, borderRadius: 8, border: "1px solid #e4e4e7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }} title="Set CIX Bearer token">🔑</button>
          <div style={{ display: "flex", gap: 2, background: "#f4f4f5", borderRadius: 8, padding: 2, marginLeft: 4 }}>
            {(["chat", "log"] as const).map((id) => (
              <button key={id} type="button" onClick={() => setView(id)} style={{ all: "unset", cursor: "pointer", padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, background: view === id ? "#fff" : "transparent", color: view === id ? "#18181b" : "#a1a1aa", boxShadow: view === id ? "0 1px 3px rgba(0,0,0,.06)" : "none", transition: "all .15s" }}>
                {id === "chat" ? "Chat" : `Log${log.length ? ` (${log.length})` : ""}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showTk && (
        <div style={{ padding: "10px 24px", background: "#fffbeb", borderBottom: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, animation: "fadeIn .2s ease" }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#92400e", whiteSpace: "nowrap" }}>CIX Bearer token</span>
          <input
            value={tkInput}
            onChange={(e) => setTkInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveTk();
            }}
            placeholder="Paste from CIP Control Plane DevTools → Network → Authorization header..."
            style={{ flex: 1, padding: "6px 12px", borderRadius: 8, border: "1px solid #fde68a", outline: "none", fontSize: 11, fontFamily: "'JetBrains Mono',monospace", background: "#fff" }}
          />
          <button type="button" onClick={saveTk} disabled={!tkInput.trim()} style={{ all: "unset", cursor: tkInput.trim() ? "pointer" : "not-allowed", padding: "6px 14px", borderRadius: 8, background: tkInput.trim() ? "#d97706" : "#e4e4e7", color: "#fff", fontSize: 12, fontWeight: 600 }}>Save</button>
          <button type="button" onClick={() => setShowTk(false)} style={{ all: "unset", cursor: "pointer", fontSize: 15, color: "#92400e", padding: "0 4px" }}>×</button>
        </div>
      )}

      {/* ── Main ── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

        {/* Chat */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", minHeight: 0 }}>

            {/* Capability cards */}
            {showCaps && msgs.length === 0 && (
              <div style={{ animation: "fadeIn .3s ease" }}>
                <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, letterSpacing: "-.01em" }}>What would you like to do?</p>
                <p style={{ fontSize: 13, color: "#71717a", marginBottom: 18 }}>Choose a capability or just describe what you need</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
                  {CAPS.map(c => (
                    <button key={c.id} className="cap-btn" onClick={() => send(CAP_PROMPTS[c.id])}
                      style={{ padding: "14px 16px", borderRadius: 12, textAlign: "left", display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{ fontSize: 20 }}>{c.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#18181b" }}>{c.label}</span>
                      <span style={{ fontSize: 11, color: "#71717a", lineHeight: 1.4 }}>{c.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {msgs.map((m, i) => (
              <div key={i} style={{ marginBottom: 12, animation: "fadeIn .2s ease", display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                {m.role === "sys" ? (
                  <div style={{ fontSize: 11, padding: "5px 12px", borderRadius: 8, alignSelf: "center", display: "flex", alignItems: "center", gap: 5,
                    background: (m.evt && evtStyle[m.evt].bg) || "#f4f4f5", color: (m.evt && evtStyle[m.evt].fg) || "#71717a" }}>
                    {m.evt === "run" && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "currentColor", animation: "pulse 1s infinite" }} />}
                    {m.evt && evtStyle[m.evt].icon !== "⟳" && <span>{evtStyle[m.evt].icon}</span>}
                    <span>{m.content}</span>
                  </div>
                ) : (
                  <div className={m.role === "user" ? "msg-user" : "msg-ai"} style={{ maxWidth: "78%", padding: "11px 16px", fontSize: 13.5, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                    {m.content}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div style={{ display: "flex", gap: 4, padding: "8px 0" }}>
                {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1", animation: `pulse 1.2s infinite ${i * .15}s` }} />)}
              </div>
            )}
            <div ref={end} />
          </div>

          {/* Input */}
          <div style={{ padding: "12px 24px 16px", borderTop: "1px solid #e4e4e7", background: "#fff", flexShrink: 0 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Describe what you want to build, trigger, or investigate..." disabled={busy}
                style={{ flex: 1, padding: "11px 16px", borderRadius: 12, border: "1.5px solid #e4e4e7", outline: "none", fontSize: 14, fontFamily: "'DM Sans',sans-serif", background: "#fafafa", transition: "all .15s" }} />
              <button onClick={() => send()} disabled={busy || !input.trim()} style={{
                all: "unset", cursor: busy ? "not-allowed" : "pointer", padding: "11px 20px", borderRadius: 12,
                background: input.trim() && !busy ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : "#e4e4e7",
                color: "#fff", fontSize: 14, fontWeight: 600, transition: "all .15s",
                boxShadow: input.trim() && !busy ? "0 4px 12px rgba(99,102,241,.25)" : "none"
              }}>Send</button>
            </div>
            {msgs.length > 0 && (
              <button type="button" onClick={() => { setMsgs([]); setShowCaps(true); }} style={{ all: "unset", cursor: "pointer", fontSize: 11, color: "#a1a1aa", marginTop: 8, display: "block" }}>
                ↻ New conversation
              </button>
            )}
          </div>
        </div>

        {view === "log" && !HIDE_CIP_PLATFORM_CHROME && (
          <div style={{ width: 420, overflowY: "auto", padding: 18, background: "#fff", borderLeft: "1px solid #e4e4e7", flexShrink: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3, letterSpacing: "-.01em" }}>API Log</div>
            <div style={{ fontSize: 11, color: "#a1a1aa", marginBottom: 14 }}>RAER + CIX HTTP calls</div>
            {log.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: "#a1a1aa", fontSize: 13 }}>No API calls yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[...log].reverse().map((e, i) => (
                  <div key={i} className="log-entry" style={{ padding: "10px 12px", borderRadius: 10, background: "#fafafa", border: "1px solid #e4e4e7", animation: "fadeIn .2s ease" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5,
                        background: e.type.includes("ERR") ? "#fef2f2" : e.type.includes("OK") ? "#ecfdf5" : "#eef2ff",
                        color: e.type.includes("ERR") ? "#991b1b" : e.type.includes("OK") ? "#065f46" : "#3730a3" }}>{e.type}</span>
                      <span style={{ fontSize: 10, color: "#a1a1aa", fontFamily: "'JetBrains Mono',monospace" }}>{new Date(e.ts).toLocaleTimeString()}</span>
                    </div>
                    {e.api && <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: "#52525b" }}>{e.api}</div>}
                    {e.headers?.eventName && (
                      <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: "#52525b" }}>
                        → {e.headers.eventName} on {e.headers.journeyName}
                      </div>
                    )}
                    {e.status !== undefined && (
                      <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: (e.status ?? 500) < 400 ? "#059669" : "#dc2626", fontWeight: 500 }}>HTTP {e.status}</div>
                    )}
                    {(e.payload !== undefined || e.body !== undefined || e.data !== undefined) && (
                      <details style={{ marginTop: 6 }}>
                        <summary style={{ fontSize: 11, color: "#71717a", cursor: "pointer" }}>Payload / Response</summary>
                        <pre style={{ fontSize: 10, fontFamily: "'JetBrains Mono',monospace", margin: "4px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 220, overflow: "auto", background: "#f4f4f5", padding: 8, borderRadius: 6, color: "#3f3f46" }}>
                          {JSON.stringify(e.payload ?? e.body ?? e.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CipAiPlatformApp;
